import {
  ClaimHistory,
  ClaimHistoryResponse,
  ClaimListFilters,
  ClaimStatuses,
  DirectionModel,
  TravelClaimComplex,
  TravelClaimFileList,
  TravelClaimListUpdateModel,
  TravelClaimModel,
  TravelClaimSimple,
  TravelClaimsList,
  TravelClaimVise,
} from "../types/travelClaims";
import { tripsAPI } from "../api";
import getPaginationParams from "../helpers/pagination";
import { buildOrdering } from "../helpers/ordering";
import { formatDateToYYYYMMDD } from "../helpers/dates";
import {
  ClaimColleaguesListResponse,
  ColleagueForSelector,
  ColleagueModel,
} from "../types/users";
import { getUserFioShort } from "../helpers/users";

export const fetchTravelClaims = (
  status: ClaimStatuses | null = null,
  ordering: {},
  search: string = "",
  filters: ClaimListFilters | undefined,
  page: number
): Promise<TravelClaimsList> => {
  // Deprecated
  let queryParams = `?${getPaginationParams(page)}`;
  if (status) {
    queryParams += `&status=${status}`;
  }

  if (search) {
    queryParams += `&search=${search}`;
  }

  if (filters) {
    if (filters.responsible) {
      queryParams += `&responsible=${filters.responsible}`;
    }

    if (filters.types) {
      let typesValue = "";
      filters.types.forEach((value: string) => {
        typesValue += value.toUpperCase() + ",";
      });
      queryParams += `&type=${typesValue}`;
    }

    if (filters.createdAtFrom) {
      queryParams += `&created_at_after=${formatDateToYYYYMMDD(
        filters.createdAtFrom
      )}`;
    }

    if (filters.createdAtTo) {
      queryParams += `&created_at_before=${formatDateToYYYYMMDD(
        filters.createdAtTo
      )}`;
    }
  }

  queryParams += "&" + buildOrdering(ordering);

  return tripsAPI.get<TravelClaimsList>(`claim/${queryParams}`).then(
    (res) => {
      return Promise.resolve(res.data);
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const fetchTravelClaimsV2 = (
  status: ClaimStatuses[] | null = null,
  ordering: {},
  search: string = "",
  filters: ClaimListFilters | undefined,
  page: number,
  limit: number | null = null, // Обычно задается внутри функции, но для некоторых случаев можно задать самому
  pageSize: number | null = null,
): Promise<TravelClaimsList> => {
  let queryParams: string;
  if (limit) {
    queryParams = `?limit=${limit}`;
  } else if (pageSize) {
    queryParams = `?${getPaginationParams(page, pageSize)}`;
  } else {
    queryParams = `?${getPaginationParams(page)}`;
  }

  if (status?.length) {
    // Если передан статус для вкладок на панели над списком
    queryParams += `&status=${status.join(",")}`;
  } else if (!status?.length && filters?.status) {
    // Если статус вкладок не передан (общая вкладка Заявки), но передан статус фильтрации
    const fs = Array.isArray(filters.status) ? filters.status.join(",") : filters.status;
    queryParams += `&status=${fs}`;
  }

  const s = search.trim();
  if (s) {
    queryParams += `&search=${encodeURIComponent(search)}`;
  }

  if (filters) {
    if (filters.responsible) {
      queryParams += `&responsible=${filters.responsible}`;
    }

    if (filters?.types?.length) {
      queryParams += `&type=${filters.types.map(v => v.toUpperCase()).join(",")}`;
    }

    if (filters.createdAtFrom) {
      queryParams += `&created_at_after=${formatDateToYYYYMMDD(
        filters.createdAtFrom
      )}`;
    }

    if (filters.createdAtTo) {
      queryParams += `&created_at_before=${formatDateToYYYYMMDD(
        filters.createdAtTo
      )}`;
    }
  }

  const ord = buildOrdering(ordering);
  if (ord) queryParams += `&${ord}`;
  queryParams += "&include_counters=1";  // для получения счетчика новых

  return tripsAPI.get<TravelClaimsList>(`v2/claim/${queryParams}`).then(
    (res) => {
      return Promise.resolve(res.data);
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const retrieveTravelClaim = (
  pk: string
): Promise<TravelClaimSimple | TravelClaimComplex | TravelClaimVise> => {
  return tripsAPI
    .get<TravelClaimSimple | TravelClaimComplex | TravelClaimVise>(
      `claim/${pk}/`
    )
    .then(
      (res) => {
        return Promise.resolve(res.data);
      },
      (error) => {
        return Promise.reject(error);
      }
    );
};

export const fetchTravelClaimFiles = (
  claim_pk: string
): Promise<TravelClaimFileList> => {
  return tripsAPI
    .get<TravelClaimFileList>(`claim/${claim_pk}/primary_files/`)
    .then(
      (res) => {
        return Promise.resolve(res.data);
      },
      (error) => {
        console.error("API error:", error);
        return Promise.reject(error);
      }
    );
};

export const deleteTravelClaimFile = (
  pk: string,
  claim_pk: string
): Promise<any> => {
  return tripsAPI.delete<any>(`claim/${claim_pk}/files/${pk}/`).then(
    (res) => {
      return Promise.resolve();
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const deleteTravelClaimPrimaryFile = (
  pk: string,
  claim_pk: string
): Promise<any> => {
  return tripsAPI.delete<any>(`claim/${claim_pk}/primary_files/${pk}/`).then(
    (res) => {
      return Promise.resolve();
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const updateTravelClaim = (
  pk: string,
  model: TravelClaimModel | TravelClaimListUpdateModel
): Promise<any> => {
  return tripsAPI.patch(`claim/${pk}/`, model).then(
    (res) => {
      return Promise.resolve(res.data);
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const returnToNew = (pk: string): Promise<TravelClaimModel> => {
  return tripsAPI.post(`claim/${pk}/return_to_new/`).then(
    (res) => {
      return Promise.resolve(res.data);
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const postponeClaimRequest = (pk: string): Promise<TravelClaimModel> => {
  return tripsAPI.post(`claim/${pk}/postpone/`).then(
    (res) => {
      return Promise.resolve(res.data);
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const setInWork = (pk: string): Promise<TravelClaimModel> => {
  return tripsAPI.post(`claim/${pk}/take_in_work_by_responsible/`).then(
    (res) => {
      return Promise.resolve(res.data);
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const finishTravelClaim = (pk: string): Promise<any> => {
  return tripsAPI.post<any>(`claim/${pk}/finish/`).then(
    (res) => {
      return Promise.resolve(res.data);
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export const addClaimDirection = (
  pk: string,
  model: DirectionModel
): Promise<DirectionModel> => {
  return tripsAPI
    .post<DirectionModel>(`claim/${pk}/add_directions/`, model)
    .then(
      (res) => {
        return Promise.resolve(res.data);
      },
      (error) => {
        return Promise.reject(error);
      }
    );
};

export const deleteClaimDirection = (
  claim_pk: string,
  pk: string
): Promise<any> => {
  return tripsAPI
    .delete<any>(`claim/${claim_pk}/delete_directions/${pk}/`)
    .then(
      (res) => {
        return Promise.resolve();
      },
      (error) => {
        return Promise.reject(error);
      }
    );
};

export const updateClaimDirection = (
  claim_pk: string,
  pk: string,
  model: DirectionModel
): Promise<DirectionModel> => {
  return tripsAPI
    .put<DirectionModel>(`claim/${claim_pk}/update_directions/${pk}/`, model)
    .then(
      (res) => {
        return Promise.resolve(res.data);
      },
      (error) => {
        return Promise.reject(error);
      }
    );
};

export const updateSimpleClaim = (
  claim_pk: string,
  directions: DirectionModel[]
): Promise<any> => {
  return Promise.allSettled(
    directions.map((direction) => {
      // Если не установлен pk, значит добавленное направление
      if (!direction.pk) {
        return addClaimDirection(claim_pk, direction);
      }
      // Если есть pk и to_delete, то удалить существующую заявку
      if (direction.pk && direction.to_delete) {
        return deleteClaimDirection(claim_pk, direction.id || "");
      }
      // Если есть pk и нет to_delete, то обновить существующую заявку
      if (direction.pk && !direction.to_delete) {
        return updateClaimDirection(claim_pk, direction.id || "", direction);
      }
    })
  );
};

export function deleteTravelClaimColleague(
  pk: string,
  claim_pk: string
): Promise<any> {
  return tripsAPI.delete<any>(`claim/${claim_pk}/delete-colleague/${pk}/`).then(
    (res) => {
      return Promise.resolve();
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}

export function addTravelClaimColleague(
  claim_pk: string,
  model: ColleagueModel
): Promise<any> {
  return tripsAPI
    .post<any>(
      `claim/${claim_pk}/add_colleagues/`,
      // Передаём colleague_pk как uuid, а не модель
      { ...model, colleague_pk: model.colleague_pk?.pk }
    )
    .then(
      (res) => {
        return Promise.resolve();
      },
      (error) => {
        return Promise.reject(error);
      }
    );
}

export function updateTravelClaimColleague(
  claim_pk: string,
  colleague_pk: string,
  model: ColleagueModel
): Promise<any> {
  return tripsAPI
    .put<any>(
      `claim/${claim_pk}/update_colleagues/${colleague_pk}/`,
      // Передаём пока только 3 поля
      {
        hotel_need: model.hotel_need,
        other_documents_need: model.other_documents_need,
        tickets_need: model.tickets_need,
      }
    )
    .then(
      (res) => {
        return Promise.resolve();
      },
      (error) => {
        return Promise.reject(error);
      }
    );
}

export function fetchClaimColleagues(
  pk: string,
  limit: number,
  offset: number,
  search?: string
): Promise<Array<ColleagueForSelector>> {
  return tripsAPI
    .get<ClaimColleaguesListResponse>(`claim/${pk}/colleagues/`, {
      params: {
        limit,
        offset,
        search,
      },
    })
    .then(
      (res) => {
        return Promise.resolve(
          res.data.results.map((e) => {
            let res: ColleagueForSelector = {
              pk: e.colleague_pk.pk,
              name: getUserFioShort(e.colleague_pk),
            };
            return res;
          })
        );
      },
      (error) => {
        return Promise.reject(error);
      }
    );
}

export function fetchClaimHistory(
  pk: string,
  limit: number,
  offset: number
): Promise<Array<ClaimHistory>> {
  return tripsAPI
    .get<ClaimHistoryResponse>(`claim/${pk}/claim_history/`, {
      params: {
        limit,
        offset,
      },
    })
    .then(
      (res) => {
        return Promise.resolve(res.data.results);
      },
      (error) => {
        return Promise.reject(error);
      }
    );
}
